import type {
  ChecklistStatus,
  ItemInstance,
  ItemPreset,
  PackingState,
  ScheduleEntry,
  TripParticipant,
} from './schema';
import { packingStateSchema, scheduleEntrySchema } from './schema';
import { FREE_RIGHTS, type Rights } from './entitlement';

const PARTICIPANT_COLORS = ['#145C55', '#E36F47', '#4569A7', '#986B2F', '#8A5A8C', '#4E7D42'];

export interface PackingGroup {
  key: string;
  presetId?: string;
  name: string;
  items: ItemInstance[];
  quantity: number;
  prepared: number;
  packed: number;
  totalWeightG: number;
  unknownWeights: number;
}

export function createParticipants(ownerNames: readonly string[], createdAt: string): TripParticipant[] {
  const names = [...new Set(ownerNames.map((name) => name.trim()).filter(Boolean))];
  if (!names.length) names.push('나');
  return names.map((name, index) => ({
    id: name,
    name,
    role: index === 0 ? 'owner' : 'traveler',
    color: PARTICIPANT_COLORS[index % PARTICIPANT_COLORS.length]!,
    createdAt,
  }));
}

export function normalizeCompleteTravelState(state: PackingState): PackingState {
  const participants = state.trip.participants?.length
    ? state.trip.participants
    : createParticipants(state.trip.ownerIds, state.trip.createdAt);
  return {
    ...state,
    trip: {
      ...state.trip,
      participants,
      ownerIds: participants.map((participant) => participant.name),
      memories: state.trip.memories ?? [],
      shareSettings: state.trip.shareSettings ?? {
        permission: 'view',
        includePacking: true,
        includePhotos: false,
        expiresInDays: 30,
      },
      scheduleEntries: (state.trip.scheduleEntries ?? []).map((entry, index) => ({
        ...entry,
        order: entry.order ?? index,
      })),
    },
  };
}

export function groupPackingItems(state: PackingState): PackingGroup[] {
  const groups = new Map<string, ItemInstance[]>();
  for (const item of state.items) {
    const key = item.presetId ? `preset:${item.presetId}` : `custom:${item.name}`;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  return [...groups.entries()].map(([key, items]) => {
    const sorted = [...items].sort((a, b) => a.quantityIndex - b.quantityIndex || a.id.localeCompare(b.id));
    return {
      key,
      presetId: sorted[0]?.presetId,
      name: sorted[0]?.name ?? '',
      items: sorted,
      quantity: sorted.length,
      prepared: sorted.filter((item) => item.checklistStatus === 'ready' || item.checklistStatus === 'packed').length,
      packed: sorted.filter((item) => item.checklistStatus === 'packed').length,
      totalWeightG: sorted.filter((item) => item.weightG !== undefined).reduce((sum, item) => sum + item.weightG!, 0),
      unknownWeights: sorted.filter((item) => item.weightG === undefined).length,
    };
  }).sort((a, b) => a.name.localeCompare(b.name, 'ko-KR'));
}

function itemRemovalPriority(item: ItemInstance) {
  const status = item.checklistStatus ?? 'planned';
  const statusScore: Record<ChecklistStatus, number> = { planned: 0, to_buy: 1, ready: 2, packed: 3 };
  return (item.assignedBagId ? 10 : 0) + statusScore[status];
}

export function setPresetQuantity(state: PackingState, preset: ItemPreset, targetQuantity: number, ownerId = state.trip.ownerIds[0] ?? '나', rights: Rights = FREE_RIGHTS): PackingState {
  return setItemQuantity(state, { ...preset, presetId: preset.id }, targetQuantity, ownerId, rights);
}

export function setItemQuantity(
  state: PackingState,
  preset: Pick<ItemInstance, 'presetId' | 'name' | 'packed' | 'packedDimensionsState' | 'weightG' | 'weightSource' | 'packMoment' | 'rotatable' | 'tags'>,
  targetQuantity: number,
  ownerId = state.trip.ownerIds[0] ?? '나',
  rights: Rights = FREE_RIGHTS,
): PackingState {
  const target = Math.max(0, Math.floor(targetQuantity));
  if (!Number.isInteger(targetQuantity) || targetQuantity < 0) throw new Error('수량은 0 이상의 정수로 입력해주세요.');
  const matches = (item: ItemInstance) => preset.presetId ? item.presetId === preset.presetId : !item.presetId && item.name === preset.name;
  const current = state.items.filter(matches);
  if (target === current.length) return state;
  if (target > current.length && state.items.length + (target - current.length) > Math.max(rights.maxItemsPerTrip, state.items.length)) {
    throw new Error(`현재 권리에서는 여행 하나에 준비물 ${rights.maxItemsPerTrip}개까지 새로 담을 수 있어요. 기존 준비물은 그대로예요.`);
  }
  if (target > current.length) {
    const additions = Array.from({ length: target - current.length }, (_, index): ItemInstance => ({
      id: `${state.trip.id}:item-${crypto.randomUUID()}`,
      tripId: state.trip.id,
      presetId: preset.presetId,
      name: preset.name,
      ownerId,
      packed: preset.packed,
      packedDimensionsState: preset.packedDimensionsState,
      weightG: preset.weightG,
      weightSource: preset.weightSource,
      packMoment: preset.packMoment,
      rotatable: preset.rotatable,
      quantityIndex: current.length + index + 1,
      tags: preset.tags,
      checklistStatus: 'planned',
      accessLevel: preset.tags.includes('first_access') ? 'quick' : 'standard',
    }));
    return {
      ...state,
      items: [...state.items, ...additions],
      trip: { ...state.trip, itemInstanceIds: [...state.trip.itemInstanceIds, ...additions.map((item) => item.id)] },
    };
  }
  const removeCount = current.length - target;
  const removeIds = new Set([...current]
    .sort((a, b) => itemRemovalPriority(a) - itemRemovalPriority(b) || b.quantityIndex - a.quantityIndex)
    .slice(0, removeCount)
    .map((item) => item.id));
  const kept = state.items.filter((item) => !removeIds.has(item.id));
  let nextIndex = 0;
  const items = kept.map((item) => matches(item) ? { ...item, quantityIndex: ++nextIndex } : item);
  return {
    ...state,
    items,
    placements: state.placements.filter((placement) => !removeIds.has(placement.itemInstanceId)),
    trip: {
      ...state.trip,
      itemInstanceIds: state.trip.itemInstanceIds.filter((id) => !removeIds.has(id)),
      extractionOrder: state.trip.extractionOrder.filter((id) => !removeIds.has(id)),
    },
  };
}

export function setPackingGroupStatus(state: PackingState, groupKey: string, status: ChecklistStatus): PackingState {
  const group = groupPackingItems(state).find((entry) => entry.key === groupKey);
  if (!group) return state;
  const ids = new Set(group.items.map((item) => item.id));
  return { ...state, items: state.items.map((item) => ids.has(item.id) ? { ...item, checklistStatus: status } : item) };
}

export function updateParticipant(state: PackingState, participant: TripParticipant): PackingState {
  const participants = state.trip.participants ?? [];
  const previous = participants.find((entry) => entry.id === participant.id);
  const nextParticipants = previous
    ? participants.map((entry) => entry.id === participant.id ? participant : entry)
    : [...participants, participant];
  return {
    ...state,
    items: state.items.map((item) => previous && item.ownerId === previous.name ? { ...item, ownerId: participant.name } : item),
    trip: { ...state.trip, participants: nextParticipants, ownerIds: nextParticipants.map((entry) => entry.name), shareSettings: state.trip.shareSettings && previous && state.trip.shareSettings.packingOwner === previous.name ? { ...state.trip.shareSettings, packingOwner: participant.name } : state.trip.shareSettings },
  };
}

export function removeParticipant(state: PackingState, participantId: string): PackingState {
  const participants = state.trip.participants ?? [];
  const target = participants.find((entry) => entry.id === participantId);
  if (!target || target.role === 'owner' || participants.length === 1) return state;
  const owner = participants.find((entry) => entry.role === 'owner') ?? participants[0]!;
  const nextParticipants = participants.filter((entry) => entry.id !== participantId);
  return {
    ...state,
    items: state.items.map((item) => ({ ...item, ...(item.ownerId === target.name || item.ownerId === target.id ? { ownerId: owner.name } : {}), ...(item.recipientId === target.id ? { recipientId: undefined } : {}) })),
    trip: {
      ...state.trip,
      participants: nextParticipants,
      ownerIds: nextParticipants.map((entry) => entry.name),
      scheduleEntries: (state.trip.scheduleEntries ?? []).map((entry) => ({ ...entry, participantIds: entry.participantIds?.filter((id) => id !== participantId) })),
      memories: (state.trip.memories ?? []).map((memory) => ({ ...memory, participantIds: memory.participantIds?.filter((id) => id !== participantId) })),
    },
  };
}

export function googleMapsHref(entry: Pick<ScheduleEntry, 'googleMapsUrl' | 'mapUrl' | 'address' | 'place'>): string | undefined {
  const link=entry.mapUrl ?? entry.googleMapsUrl;
  if (link && isMapUrl(link)) return link;
  const query = entry.address?.trim() || entry.place?.trim();
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : undefined;
}

export function isMapUrl(value: string) {
  if (!value.trim()) return true;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return false;
    return ['map.naver.com','naver.me','map.kakao.com','kko.to','place.map.kakao.com'].includes(url.hostname) || url.hostname === 'maps.app.goo.gl'
      || (url.hostname === 'goo.gl' && url.pathname.startsWith('/maps/'))
      || (['www.google.com', 'google.com', 'www.google.co.kr', 'www.google.co.jp'].includes(url.hostname) && /^\/maps(?:\/|$)/.test(url.pathname))
      || ['maps.google.com', 'maps.google.co.kr', 'maps.google.co.jp'].includes(url.hostname);
  } catch {
    return false;
  }
}

export function validateSchedule(entry: ScheduleEntry): ScheduleEntry {
  const result = scheduleEntrySchema.safeParse({ ...entry, title: entry.title.trim() });
  if (!result.success) throw new Error('일정 이름과 날짜·시각을 확인해주세요.');
  if (!isMapUrl(entry.mapUrl ?? entry.googleMapsUrl ?? '')) throw new Error('네이버·카카오·구글 지도에서 복사한 https 링크만 저장해요. 주소만 남겨도 지도를 열 수 있어요.');
  if (entry.endDate && entry.date && entry.endDate < entry.date) throw new Error('종료일은 시작일 이후로 정해주세요.');
  if ((!entry.endDate || entry.endDate === entry.date) && entry.time && entry.endTime && entry.endTime < entry.time) throw new Error('종료 시각은 시작 이후로 정하거나 다음 날짜를 입력해주세요.');
  return result.data;
}

export function removeSchedule(state: PackingState, id: string): PackingState {
  return { ...state, trip: { ...state.trip,
    scheduleEntries: (state.trip.scheduleEntries ?? []).filter((entry) => entry.id !== id),
    memories: (state.trip.memories ?? []).map((memory) => memory.linkedScheduleEntryId === id ? { ...memory, linkedScheduleEntryId: undefined } : memory),
  } };
}

export function dayLabel(date?: string) {
  if (!date) return '날짜 미정';
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' }).format(parsed);
}

export function adoptSharedTrip(
  shared: PackingState,
  share: { shareId: string; encryptionKey: string; editToken?: string; revision: number; expiresAt: string },
  nextTripId = `trip-${crypto.randomUUID()}`,
  now = new Date().toISOString(),
): PackingState {
  const bagIds = new Map(shared.bags.map((bag, index) => [bag.id, `${nextTripId}:bag-${index + 1}`]));
  const containerIds = new Map(shared.containers.map((container, index) => [container.id, `${nextTripId}:container-${index + 1}`]));
  const itemIds = new Map(shared.items.map((item, index) => [item.id, `${nextTripId}:item-${index + 1}`]));
  const bags = shared.bags.map((bag) => ({ ...bag, id: bagIds.get(bag.id)!, tripId: nextTripId, containerIds: bag.containerIds.flatMap((id) => containerIds.get(id) ?? []) }));
  const containers = shared.containers.map((container) => ({ ...container, id: containerIds.get(container.id)!, bagId: bagIds.get(container.bagId)!, parentContainerId: container.parentContainerId ? containerIds.get(container.parentContainerId) : undefined }));
  const items = shared.items.map((item) => ({ ...item, id: itemIds.get(item.id)!, tripId: nextTripId, assignedBagId: item.assignedBagId ? bagIds.get(item.assignedBagId) : undefined, placementId: undefined }));
  const state: PackingState = {
    ...shared,
    trip: {
      ...shared.trip,
      id: nextTripId,
      sourceTripId: shared.trip.id,
      status: 'active',
      bagIds: shared.trip.bagIds.flatMap((id) => bagIds.get(id) ?? []),
      itemInstanceIds: shared.trip.itemInstanceIds.flatMap((id) => itemIds.get(id) ?? []),
      extractionOrder: shared.trip.extractionOrder.flatMap((id) => itemIds.get(id) ?? []),
      createdAt: now,
      updatedAt: now,
      scheduleEntries: shared.trip.scheduleEntries?.map((entry, index) => ({ ...entry, id: `${nextTripId}:schedule-${index + 1}` })),
      memories: shared.trip.memories?.map((memory, index) => ({ ...memory, id: `${nextTripId}:memory-${index + 1}`, linkedScheduleEntryId: undefined })),
      remoteShare: share.editToken ? {
        shareId: share.shareId,
        editToken: share.editToken,
        encryptionKey: share.encryptionKey,
        revision: share.revision,
        permission: 'edit',
        expiresAt: share.expiresAt,
        lastSyncedAt: now,
      } : undefined,
    },
    bags,
    containers,
    items,
    placements: [],
  };
  return packingStateSchema.parse(normalizeCompleteTravelState(state));
}

/** Backward compatible name for older consumers. */
export const isGoogleMapsUrl = isMapUrl;
export function mapSearchLinks(entry: Pick<ScheduleEntry,'address'|'place'>) { const query=entry.address?.trim()||entry.place?.trim();return query ? { naver:`https://map.naver.com/p/search/${encodeURIComponent(query)}`, kakao:`https://map.kakao.com/link/search/${encodeURIComponent(query)}` } : undefined; }
